import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FaCar,
  FaCheck,
  FaPlane,
  FaStar,
} from "react-icons/fa";
import { getDestinationHeroImage } from "../data/destinationHeroImages";
import { trackBookingClick, trackWhatsAppClick } from "../utils/analytics";

const whatsappLink =
  "https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi.";

const hotelAreas = [
  {
    label: "Waterfront living",
    name: "Limassol Marina",
    description:
      "A polished waterfront district beside the historic center, with a promenade, dining, shops, and an easy walk into Old Town. It feels lively in the evening while still working well for a relaxed city break.",
    bestFor: "Couples, luxury stays, dining, and nightlife",
    attractions: "Marina promenade, Old Port, Limassol Castle, and Molos",
    hotelStyle: "Upscale waterfront stays and premium apartments",
    price: "€150–€350+",
    transfer:
      "Central and straightforward for airport drop-off; share the exact marina property or entrance when booking.",
    distanceFeel: "Central and walkable",
  },
  {
    label: "Historic center",
    name: "Old Town",
    description:
      "The best base for visitors who want character, local food, independent shops, and historic streets close at hand. The beach is not the main focus, but Molos and the seafront are still within comfortable reach.",
    bestFor: "Culture, food, short breaks, and car-free exploring",
    attractions: "Limassol Castle, Old Market, Saripolou Square, and Molos",
    hotelStyle: "Boutique hotels, guesthouses, and city apartments",
    price: "€80–€180",
    transfer:
      "Some historic streets have limited vehicle access, so confirm the closest practical hotel entrance or pickup point.",
    distanceFeel: "In the city center",
  },
  {
    label: "Beach meets city",
    name: "Enaerios",
    description:
      "A convenient seafront choice between the city center and the eastern beach districts. It suits travelers who want the coast nearby without feeling separated from central Limassol.",
    bestFor: "First visits, business trips, and beach-and-city stays",
    attractions: "Enaerios seafront, Molos promenade, cafes, and city museums",
    hotelStyle: "Modern city hotels and seafront accommodation",
    price: "€90–€220",
    transfer:
      "Easy door-to-door access from either airport and a useful base for reaching both central and eastern Limassol.",
    distanceFeel: "Near center, on seafront",
  },
  {
    label: "Lively resort strip",
    name: "Germasogeia Tourist Area",
    description:
      "A well-established visitor area with beaches, restaurants, bars, supermarkets, and plenty of holiday services. It is livelier than the city center in summer and spreads along a longer coastal strip.",
    bestFor: "Nightlife, families, longer stays, and convenience",
    attractions: "Coastal beaches, restaurants, bars, and Dasoudi nearby",
    hotelStyle: "Resort hotels, aparthotels, and holiday apartments",
    price: "€100–€280",
    transfer:
      "Tell the driver the full hotel address because the tourist area is long and similarly named properties can be far apart.",
    distanceFeel: "East of center, resort feel",
  },
  {
    label: "Relaxed beachfront",
    name: "Dasoudi",
    description:
      "A greener, calmer seafront area centered on a popular beach and eucalyptus-lined park. It offers an easygoing holiday atmosphere while keeping restaurants and the tourist area close.",
    bestFor: "Families, beach time, walking, and quieter evenings",
    attractions: "Dasoudi Beach, coastal path, park, and nearby dining",
    hotelStyle: "Beach hotels, apartments, and relaxed mid-range stays",
    price: "€90–€220",
    transfer:
      "Direct hotel transfer is convenient, especially with children or beach luggage; provide the exact property pin if available.",
    distanceFeel: "Beachside, short ride to center",
  },
  {
    label: "Quiet luxury",
    name: "Amathus / Agios Tychonas",
    description:
      "Limassol's quieter eastern resort zone, known for spacious beachfront properties, wellness facilities, and a more self-contained holiday experience. It is farther from Old Town but close to ancient Amathus.",
    bestFor: "Luxury, spa breaks, families, and resort holidays",
    attractions: "Amathus archaeological site, beaches, and coastal paths",
    hotelStyle: "Large beachfront resorts and luxury spa hotels",
    price: "€160–€400+",
    transfer:
      "Usually the easternmost hotel area, so the exact resort location should be confirmed when requesting a transfer quote.",
    distanceFeel: "Quiet resort edge",
  },
];

const popularHotels = [
  {
    name: "Four Seasons Hotel",
    area: "Agios Tychonas",
    stars: 5,
    price: "€300–€600+",
    bestFor: "Luxury beach stays, couples, and fine dining",
    description:
      "An established beachfront resort with polished service, landscaped pools, spa facilities, and a strong choice of restaurants.",
  },
  {
    name: "Parklane Resort & Spa",
    area: "Pyrgos",
    stars: 5,
    price: "€300–€700+",
    bestFor: "Families, spa breaks, villas, and resort holidays",
    description:
      "A spacious luxury resort east of Limassol with extensive gardens, pools, a large spa, family facilities, and direct beach access.",
  },
  {
    name: "Amathus Beach Hotel",
    area: "Agios Tychonas",
    stars: 5,
    price: "€250–€550+",
    bestFor: "Beachfront luxury, families, and relaxing stays",
    description:
      "A classic seafront resort near ancient Amathus, offering pools, gardens, wellness facilities, and a calm eastern Limassol setting.",
  },
  {
    name: "Crowne Plaza Limassol",
    area: "Enaerios / Neapolis",
    stars: 4,
    price: "€140–€280",
    bestFor: "Business travel, city access, and short beach stays",
    description:
      "A central seafront hotel that balances beach access with a convenient position for the city center and business districts.",
  },
  {
    name: "Royal Apollonia",
    area: "Germasogeia Tourist Area",
    stars: 5,
    price: "€180–€350+",
    bestFor: "Couples, beach holidays, dining, and nightlife",
    description:
      "A beachfront hotel in the lively tourist area, close to restaurants and bars while retaining a relaxed resort atmosphere.",
  },
  {
    name: "Ajax Hotel",
    area: "Mesa Geitonia",
    stars: 4,
    price: "€100–€200",
    bestFor: "Business trips, value, and city breaks",
    description:
      "A practical city hotel set inland from the seafront, with leisure and business facilities and convenient road access across Limassol.",
  },
];

const faqs = [
  {
    question: "What is the best area to stay in Limassol?",
    answer:
      "For a first visit, Enaerios is a balanced beach-and-city base, while Limassol Marina and Old Town are best for a central, walkable stay. Choose Germasogeia or Dasoudi for a more traditional beach holiday.",
  },
  {
    question: "Which area is best for luxury hotels?",
    answer:
      "Amathus and Agios Tychonas have the strongest resort feel, with larger beachfront properties and spa facilities. Limassol Marina is another premium option for visitors who prefer dining and nightlife within walking distance.",
  },
  {
    question: "Which area is best for nightlife?",
    answer:
      "Germasogeia Tourist Area has a lively mix of bars and holiday venues, especially in summer. Limassol Marina and nearby Old Town are better for restaurants, cocktail bars, and a more central evening atmosphere.",
  },
  {
    question: "Which area is best for families?",
    answer:
      "Dasoudi and Germasogeia are practical for families because they combine beach access, restaurants, shops, and varied accommodation. Agios Tychonas also works well for families wanting a quieter resort stay.",
  },
  {
    question: "Are Limassol hotels near the beach?",
    answer:
      "Many hotels in Enaerios, Germasogeia, Dasoudi, and Amathus are on or close to the seafront. Old Town properties are better for culture and dining, though the Molos promenade and coast remain nearby.",
  },
  {
    question: "How much do hotels in Limassol cost?",
    answer:
      "Indicative rates range from about €80 per night for simpler central stays to €400+ for premium beachfront resorts. Prices change significantly with season, room type, booking date, and availability.",
  },
  {
    question: "How much is a taxi from Larnaca Airport to Limassol hotels?",
    answer:
      "Private taxi transfers from Larnaca Airport to Limassol hotels start from €70. Confirm the exact hotel area, passenger count, and luggage when booking.",
  },
  {
    question: "How much is a taxi from Paphos Airport to Limassol hotels?",
    answer:
      "Private taxi transfers from Paphos Airport to Limassol hotels start from €75. The exact quote can depend on the hotel location, passengers, luggage, and any special requirements.",
  },
];

const cardClass =
  "rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] shadow-xl shadow-black/25";

function SectionHeader({ eyebrow, title, text, centered = false }) {
  return (
    <div className={`mb-12 max-w-3xl ${centered ? "mx-auto text-center" : ""}`}>
      <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
        {eyebrow}
      </p>
      <h2 className="mt-4 text-3xl font-black tracking-tight text-white md:text-5xl">
        {title}
      </h2>
      {text && <p className="mt-5 leading-8 text-neutral-300">{text}</p>}
    </div>
  );
}

export default function LimassolHotelsPage() {
  const heroImage = getDestinationHeroImage("limassol-hotels");

  return (
    <main className="min-h-screen overflow-hidden bg-[#030712] text-white">
      <Helmet>
        <title>Limassol Hotels Guide | Best Areas, Prices & Airport Transfers</title>
        <meta
          name="description"
          content="Explore the best hotel areas in Limassol, expected hotel prices, nearby attractions, and private airport taxi transfers from Larnaca Airport and Paphos Airport."
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Limassol Hotels Guide | Best Areas, Prices & Airport Transfers"
        />
        <meta
          property="og:description"
          content="Explore the best hotel areas in Limassol, expected hotel prices, nearby attractions, and private airport taxi transfers from Larnaca Airport and Paphos Airport."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://limassolairporttaxi.com/limassol-hotels"
        />
        <meta
          property="og:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <link
          rel="canonical"
          href="https://limassolairporttaxi.com/limassol-hotels"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      <section className="relative overflow-hidden px-6 pb-20 pt-36 md:pb-28 md:pt-44">
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black/15" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_18%,rgba(250,204,21,0.14),transparent_34%),linear-gradient(115deg,rgba(0,0,0,0.5),rgba(0,0,0,0.35)_55%,rgba(0,0,0,0.5))]" />
        <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#030712] to-transparent" />

        <div className="relative mx-auto max-w-7xl">
          <nav className="mb-8 flex items-center gap-2 text-sm text-neutral-300">
            <Link to="/" className="hover:text-yellow-300">
              Home
            </Link>
            <span className="text-neutral-500">&gt;</span>
            <span className="text-yellow-300">Limassol Hotels</span>
          </nav>

          <div className="grid items-center gap-10 lg:grid-cols-[1.08fr_0.92fr]">
            <div>
              <p className="inline-flex rounded-full border border-yellow-400/30 bg-yellow-400/10 px-5 py-2 text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
                Plan your Limassol stay
              </p>
              <h1 className="mt-6 text-4xl font-black leading-[1.02] tracking-tight md:text-7xl">
                Limassol Hotels Guide
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-neutral-200">
                A practical guide to the best areas to stay in Limassol, hotel
                price ranges, nearby attractions, and airport taxi transfers
                from Larnaca and Paphos airports.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href="#areas"
                  className="inline-flex items-center justify-center rounded-2xl bg-yellow-400 px-8 py-4 font-extrabold text-black shadow-xl shadow-yellow-400/20 transition hover:-translate-y-1 hover:bg-yellow-300"
                >
                  Compare Areas
                </a>
                <a
                  href="#airport-transfers"
                  className="inline-flex items-center justify-center rounded-2xl border border-yellow-400/30 bg-black/25 px-8 py-4 font-extrabold text-white backdrop-blur transition hover:-translate-y-1 hover:border-yellow-400/60 hover:text-yellow-300"
                >
                  View Airport Transfers
                </a>
              </div>
            </div>

            <aside className="rounded-[2rem] border border-yellow-400/25 bg-black/40 p-6 shadow-2xl shadow-black/40 backdrop-blur-md">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
                At a glance
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {[
                  ["Areas compared", "6"],
                  ["Typical prices", "€80–€400+"],
                  ["Larnaca transfer", "From €70"],
                  ["Paphos transfer", "From €75"],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-2xl border border-white/10 bg-white/[0.07] p-5"
                  >
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-400">
                      {label}
                    </p>
                    <p className="mt-2 text-xl font-black text-white">{value}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-sm leading-6 text-neutral-300">
                Prices are approximate nightly ranges and can rise during
                summer, holidays, and major events.
              </p>
            </aside>
          </div>
        </div>
      </section>

      <section id="areas" className="mx-auto max-w-7xl scroll-mt-28 px-6 py-20 md:py-24">
        <SectionHeader
          eyebrow="Area guide"
          title="Where to Stay in Limassol"
          text="Limassol stretches along the coast, and the right area can shape your whole trip. Compare the atmosphere, nearby sights, accommodation style, and transfer practicalities before you book."
        />
        <div className="grid gap-6 lg:grid-cols-2">
          {hotelAreas.map((area) => (
            <article key={area.name} className={`${cardClass} p-6 md:p-8`}>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-yellow-300">
                {area.label}
              </p>
              <h3 className="mt-3 text-2xl font-black md:text-3xl">{area.name}</h3>
              <p className="mt-4 leading-7 text-neutral-300">{area.description}</p>
              <dl className="mt-6 grid gap-4 border-t border-white/10 pt-6 sm:grid-cols-2">
                {[
                  ["Best for", area.bestFor],
                  ["Nearby attractions", area.attractions],
                  ["Hotel style", area.hotelStyle],
                  ["Approx. price / night", area.price],
                ].map(([term, detail]) => (
                  <div key={term}>
                    <dt className="text-xs font-bold uppercase tracking-[0.15em] text-yellow-300">
                      {term}
                    </dt>
                    <dd className="mt-2 text-sm font-semibold leading-6 text-neutral-200">
                      {detail}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6 rounded-2xl border border-yellow-400/20 bg-yellow-400/[0.07] p-4">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-yellow-300">
                  <FaCar /> Airport transfer note
                </p>
                <p className="mt-2 text-sm leading-6 text-neutral-300">
                  {area.transfer}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-[#09090b] px-6 py-20 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeader
            eyebrow="At-a-glance"
            title="Quick Comparison Table"
            text="Use this overview to narrow down your shortlist, then check the detailed area notes above."
          />
          <div className="overflow-x-auto rounded-[2rem] border border-white/10 shadow-2xl shadow-black/25">
            <table className="w-full min-w-[850px] border-collapse text-left">
              <thead className="bg-yellow-400 text-black">
                <tr>
                  {["Area", "Best For", "Hotel Style", "Approx. Price/Night", "Distance Feel"].map(
                    (heading) => (
                      <th
                        key={heading}
                        scope="col"
                        className="px-5 py-4 text-sm font-black"
                      >
                        {heading}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {hotelAreas.map((area, index) => (
                  <tr
                    key={area.name}
                    className={`border-t border-white/10 ${
                      index % 2 === 0 ? "bg-white/[0.055]" : "bg-white/[0.025]"
                    }`}
                  >
                    <th scope="row" className="px-5 py-5 font-black text-white">
                      {area.name}
                    </th>
                    <td className="px-5 py-5 text-sm leading-6 text-neutral-300">
                      {area.bestFor}
                    </td>
                    <td className="px-5 py-5 text-sm leading-6 text-neutral-300">
                      {area.hotelStyle}
                    </td>
                    <td className="px-5 py-5 font-bold text-yellow-300">
                      {area.price}
                    </td>
                    <td className="px-5 py-5 text-sm text-neutral-300">
                      {area.distanceFeel}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm leading-6 text-neutral-400">
            Indicative room rates only. Season, dates, room type, occupancy,
            and booking conditions can change the final price.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <SectionHeader
          eyebrow="Featured stays"
          title="Popular Hotels in Limassol"
          text="A selection of well-known Limassol hotels across the city's beachfront resort areas and central districts. Typical prices are approximate and vary by season, room type, and booking date."
        />
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {popularHotels.map((hotel) => (
            <article
              key={hotel.name}
              className={`${cardClass} flex h-full flex-col p-6 transition-all duration-300 hover:-translate-y-1 hover:border-yellow-400/45 hover:shadow-yellow-400/10 md:p-7`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-yellow-300">
                    {hotel.area}
                  </p>
                  <h3 className="mt-3 text-2xl font-black text-white">
                    {hotel.name}
                  </h3>
                </div>
                <div
                  className="flex shrink-0 items-center gap-1 rounded-full border border-yellow-400/25 bg-yellow-400/10 px-3 py-2 text-sm font-black text-yellow-300"
                  aria-label={`${hotel.stars} star hotel`}
                >
                  <FaStar />
                  {hotel.stars}
                </div>
              </div>

              <p className="mt-5 leading-7 text-neutral-400">
                {hotel.description}
              </p>

              <dl className="mt-6 grid gap-4 border-t border-white/10 pt-5 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.15em] text-yellow-300">
                    Typical price / night
                  </dt>
                  <dd className="mt-2 font-black text-white">{hotel.price}</dd>
                </div>
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.15em] text-yellow-300">
                    Best for
                  </dt>
                  <dd className="mt-2 text-sm font-semibold leading-6 text-neutral-200">
                    {hotel.bestFor}
                  </dd>
                </div>
              </dl>

              <div className="mt-auto pt-6">
                <div className="rounded-2xl border border-yellow-400/20 bg-yellow-400/[0.07] p-4">
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-yellow-300">
                    <FaCar /> Airport transfer
                  </p>
                  <p className="mt-2 text-sm leading-6 text-neutral-300">
                    Private door-to-door transfer available from Larnaca or
                    Paphos Airport.
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section
        id="airport-transfers"
        className="scroll-mt-28 bg-[#09090b] px-6 py-20 md:py-24"
      >
        <div className="mx-auto max-w-7xl">
          <SectionHeader
            eyebrow="Arrive without the guesswork"
            title="Airport Taxi Transfers to Limassol Hotels"
            text="Book a private transfer from either Cyprus airport directly to your hotel reception or agreed accommodation entrance."
          />
          <div className="grid gap-6 lg:grid-cols-2">
            {[
              {
                airport: "Larnaca Airport",
                price: "From €70",
                to: "/larnaca-airport-to-limassol",
              },
              {
                airport: "Paphos Airport",
                price: "From €75",
                to: "/paphos-airport-to-limassol",
              },
            ].map((route) => (
              <article key={route.airport} className={`${cardClass} p-7 md:p-8`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold uppercase tracking-[0.16em] text-yellow-300">
                      Private airport taxi
                    </p>
                    <h3 className="mt-3 text-2xl font-black">
                      {route.airport} to Limassol hotels
                    </h3>
                  </div>
                  <FaPlane className="mt-1 shrink-0 text-2xl text-yellow-300" />
                </div>
                <p className="mt-5 text-4xl font-black text-yellow-400">
                  {route.price}
                </p>
                <ul className="mt-6 grid gap-3 text-neutral-300">
                  {[
                    "Door-to-door hotel pickup",
                    "Flight monitoring included",
                    "Fast WhatsApp booking",
                    "Private transfer, not a shared shuttle",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-[10px] text-black">
                        <FaCheck />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={route.to}
                  className="mt-7 inline-flex font-bold text-yellow-300 transition hover:text-yellow-200"
                >
                  View route details →
                </Link>
              </article>
            ))}
          </div>

          <div className="mt-8 rounded-[2rem] border border-yellow-400/25 bg-gradient-to-br from-yellow-400/20 via-white/[0.055] to-black/30 p-8 md:p-10">
            <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
              <div>
                <h3 className="text-2xl font-black md:text-3xl">
                  Send your flight and hotel details for confirmation
                </h3>
                <p className="mt-4 max-w-3xl leading-7 text-neutral-300">
                  Include your flight number, landing time, passenger count,
                  luggage, and full hotel address. For Old Town or marina
                  accommodation, an entrance name or map pin can also help.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                <a
                  href={whatsappLink}
                  onClick={() => trackWhatsAppClick("hotels_transfer")}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex justify-center rounded-2xl bg-yellow-400 px-8 py-4 font-extrabold text-black transition hover:-translate-y-1 hover:bg-yellow-300"
                >
                  Book on WhatsApp
                </a>
                <a
                  href="/#booking"
                  onClick={() => trackBookingClick("hotels_transfer")}
                  className="inline-flex justify-center rounded-2xl border border-yellow-400/30 bg-black/20 px-8 py-4 font-extrabold transition hover:border-yellow-400/60 hover:text-yellow-300"
                >
                  Booking Form
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20 md:py-24">
        <SectionHeader
          eyebrow="FAQ"
          title="Limassol Hotels Guide FAQ"
          centered
        />
        <div className="grid gap-5">
          {faqs.map((faq) => (
            <article key={faq.question} className={`${cardClass} p-6 md:p-7`}>
              <h3 className="flex gap-3 text-xl font-black text-yellow-300">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-xs text-black">
                  ?
                </span>
                {faq.question}
              </h3>
              <p className="mt-4 leading-7 text-neutral-400 md:pl-9">
                {faq.answer}
              </p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
